/**
 * DossierProgressDialog — Modal showing live progress of the
 * DIAN ZIP dossier generation (file-by-file with status icons).
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, FileArchive, Clock } from "lucide-react";
import type { DossierProgress } from "@/lib/dossierExport";

interface Props {
  open: boolean;
  progress: DossierProgress;
  busy: boolean;
  onClose: () => void;
}

export function DossierProgressDialog({ open, progress, busy, onClose }: Props) {
  const pct =
    progress.totalFiles > 0
      ? Math.round((progress.completedFiles / progress.totalFiles) * 100)
      : progress.phase === "done"
      ? 100
      : 0;

  const okCount = progress.files.filter((f) => f.status === "ok").length;
  const failCount = progress.files.filter((f) => f.status === "failed").length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileArchive className="h-5 w-5 text-primary" />
            Generando Dossier DIAN
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 space-y-3 border-b">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {progress.message ?? "Preparando…"}
            </span>
            <span className="tabular-nums font-medium">
              {progress.completedFiles} / {progress.totalFiles} ({pct}%)
            </span>
          </div>
          <Progress value={pct} className="h-2" />
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
              {okCount} OK
            </span>
            {failCount > 0 && (
              <span className="flex items-center gap-1">
                <XCircle className="h-3 w-3 text-destructive" />
                {failCount} fallido(s)
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Fase: {labelForPhase(progress.phase)}
            </span>
          </div>
        </div>

        <ScrollArea className="max-h-[320px] px-2 py-2">
          {progress.files.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-8">
              Recopilando documentos del embarque…
            </div>
          ) : (
            <ul className="space-y-1">
              {progress.files.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-3 px-3 py-1.5 rounded-md text-[12px] hover:bg-muted/40"
                >
                  <StatusIcon status={f.status} />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono truncate text-foreground">{f.zipPath}</p>
                    {f.errorMessage && (
                      <p className="text-[10px] text-destructive truncate">
                        {f.errorMessage}
                      </p>
                    )}
                  </div>
                  {f.bytes && (
                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                      {formatBytes(f.bytes)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>

        <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={busy}
          >
            {progress.phase === "done" ? "Cerrar" : busy ? "Procesando…" : "Cerrar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusIcon({ status }: { status: "pending" | "downloading" | "ok" | "failed" }) {
  switch (status) {
    case "ok":
      return <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
    case "downloading":
      return <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />;
    default:
      return <Clock className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

function labelForPhase(p: DossierProgress["phase"]): string {
  switch (p) {
    case "preparing": return "Preparando";
    case "downloading": return "Descargando";
    case "compressing": return "Comprimiendo";
    case "done": return "Completado";
    case "error": return "Error";
  }
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
